import type {
	TeamworkGraphPersonRelationship,
	TeamworkGraphPersonRelationshipDirection,
} from '@confluence/twg-graph-explorer/entry-points/teamworkGraphModel.ts';

import {
	isPagePeopleRelationship,
	isPagePeopleRelationshipDirection,
} from './pagePeopleRelationships.ts';

export type PagePersonRelationshipEvidence = {
	relationship: TeamworkGraphPersonRelationship;
	direction: TeamworkGraphPersonRelationshipDirection;
	lastUpdated: number | null;
};

export type PagePerson = {
	ari: string;
	accountId: string;
	name: string;
	picture: string | null;
	jobTitle?: string;
	relationships: readonly PagePersonRelationshipEvidence[];
};

type QueryUser =
	| {
			__typename: 'AtlassianAccountUser';
			id: string;
			accountId: string;
			accountStatus: string;
			name: string;
			picture: string;
			extendedProfile?: { jobTitle?: string | null } | null;
	  }
	| {
			__typename: 'CustomerUser';
			id: string;
			accountId: string;
			accountStatus: string;
			name: string;
			picture: string;
	  }
	| { __typename: string };

type QueryValue = {
	id?: string;
	data?: QueryUser | null;
	stringValue?: string;
	timestampValue?: number;
};

type QueryColumns = ReadonlyArray<{
	readonly key: string;
	readonly value?: QueryValue | null;
}>;

export type PagePeopleQueryData = {
	readonly graphStore_cypherQueryV2?: {
		readonly edges: ReadonlyArray<{
			readonly node: {
				readonly columns: QueryColumns;
			};
		}>;
	} | null;
};

const readColumn = (columns: QueryColumns, key: string): QueryValue | undefined =>
	columns.find((column) => column.key === key)?.value ?? undefined;

const isHydratedPerson = (
	user: QueryUser | null | undefined,
): user is Extract<QueryUser, { __typename: 'AtlassianAccountUser' | 'CustomerUser' }> =>
	(user?.__typename === 'AtlassianAccountUser' || user?.__typename === 'CustomerUser') &&
	'accountStatus' in user &&
	user.accountStatus === 'active';

/**
 * Converts Graph Store's column-oriented result into people and retains all relationship evidence
 * when the same account is returned by more than one registry branch.
 */
export const parsePagePeople = (data: PagePeopleQueryData | undefined): PagePerson[] => {
	const peopleByAccount = new Map<string, PagePerson>();
	const evidenceKeysByAccount = new Map<string, Set<string>>();

	for (const edge of data?.graphStore_cypherQueryV2?.edges ?? []) {
		const columns = edge.node.columns;
		const personValue = readColumn(columns, 'person');
		const user = personValue?.data;
		const relationship = readColumn(columns, 'relationship')?.stringValue;
		const direction = readColumn(columns, 'direction')?.stringValue;

		if (
			!personValue?.id ||
			!isHydratedPerson(user) ||
			!isPagePeopleRelationship(relationship) ||
			!isPagePeopleRelationshipDirection(direction)
		) {
			continue;
		}

		const accountKey = user.accountId || personValue.id;
		const evidence: PagePersonRelationshipEvidence = {
			relationship,
			direction,
			lastUpdated: readColumn(columns, 'relationshipTimestamp')?.timestampValue ?? null,
		};
		const existing = peopleByAccount.get(accountKey);
		const evidenceKey = `${evidence.relationship}:${evidence.direction}:${evidence.lastUpdated ?? ''}`;
		// Get-or-create rather than optional chaining: a missing Set would otherwise
		// silently drop the key while still merging the relationship, so the next
		// duplicate row would be merged a second time.
		let evidenceKeys = evidenceKeysByAccount.get(accountKey);
		if (!evidenceKeys) {
			evidenceKeys = new Set<string>();
			evidenceKeysByAccount.set(accountKey, evidenceKeys);
		}

		if (existing) {
			if (evidenceKeys.has(evidenceKey)) {
				continue;
			}
			evidenceKeys.add(evidenceKey);
			peopleByAccount.set(accountKey, {
				...existing,
				relationships: [...existing.relationships, evidence],
			});
			continue;
		}

		evidenceKeys.add(evidenceKey);
		peopleByAccount.set(accountKey, {
			ari: personValue.id,
			accountId: user.accountId,
			name: user.name,
			picture: user.picture,
			...(user.__typename === 'AtlassianAccountUser' && user.extendedProfile?.jobTitle
				? { jobTitle: user.extendedProfile.jobTitle }
				: {}),
			relationships: [evidence],
		});
	}

	return [...peopleByAccount.values()];
};
